import type { GetMessagesQuery } from '#src/graphql/generated';

import {
  Button,
  IconButton,
  Popover,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FiMessageSquare } from 'react-icons/fi';

export type ThreadPickerRoot = GetMessagesQuery['messages'][number];

const SNIPPET_MAX_CHARS = 96;

function snippet(text: string): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= SNIPPET_MAX_CHARS) return t || '(empty)';
  return `${t.slice(0, SNIPPET_MAX_CHARS)}…`;
}

function formatShortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

export interface DocumentThreadPickerProps {
  roots: ThreadPickerRoot[];
  focusedRootId: string | null;
  onSelectRoot: (rootId: string) => void;
  onClearThreadFocus: () => void;
  disabled?: boolean;
}

/**
 * Popover to pick which document message thread to continue, with a short preview of each root.
 */
export function DocumentThreadPicker({
  roots,
  focusedRootId,
  onSelectRoot,
  onClearThreadFocus,
  disabled,
}: DocumentThreadPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      positioning={{ placement: 'top-end' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <IconButton
          type="button"
          variant="outline"
          size="sm"
          aria-label="Choose message thread"
          title="Choose message thread"
          disabled={disabled}
          style={{
            height: '100%',
            minHeight: '100%',
            alignSelf: 'stretch',
          }}
        >
          <FiMessageSquare size={18} strokeWidth={2} aria-hidden />
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            p={2}
            minW="260px"
            maxW="min(90vw, 360px)"
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={2} align="stretch">
              <Text fontSize="xs" color="fg.muted" px={1}>
                Message threads
              </Text>
              <Button
                size="xs"
                variant={focusedRootId ? 'ghost' : 'solid'}
                colorScheme={focusedRootId ? 'gray' : 'blue'}
                justifyContent="flex-start"
                fontWeight={focusedRootId ? 'normal' : 'semibold'}
                onClick={() => {
                  onClearThreadFocus();
                  setOpen(false);
                }}
              >
                All threads / new message
              </Button>
              {roots.length === 0 ? (
                <Text fontSize="xs" color="fg.muted" px={1} py={1}>
                  No threads yet. Send a message to start one.
                </Text>
              ) : (
                roots.map((root) => {
                  const label = snippet(root.value);
                  const when = formatShortDate(root.createdAt);
                  const isActive = focusedRootId === root.id;
                  return (
                    <Button
                      key={root.id}
                      size="xs"
                      variant={isActive ? 'solid' : 'ghost'}
                      colorScheme={isActive ? 'blue' : 'gray'}
                      justifyContent="flex-start"
                      fontWeight={isActive ? 'semibold' : 'normal'}
                      whiteSpace="normal"
                      h="auto"
                      py={1.5}
                      textAlign="left"
                      onClick={() => {
                        onSelectRoot(root.id);
                        setOpen(false);
                      }}
                    >
                      <VStack gap={0} align="stretch" width="100%">
                        <Text fontSize="xs" lineClamp={3}>
                          {label}
                        </Text>
                        {when ? (
                          <Text fontSize="2xs" color="fg.muted">
                            {when}
                          </Text>
                        ) : null}
                      </VStack>
                    </Button>
                  );
                })
              )}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
